import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Layout } from './components/Layout/Layout';
import { Login } from './pages/Login/Login';
import { AuthCallback } from './pages/AuthCallback/AuthCallback';
import { Dashboard } from './pages/Dashboard/Dashboard';
import { Start } from './pages/Start/Start';
import { MailAssistent } from './pages/Assistent/MailAssistent';
import { MeineMails } from './pages/MeineMails/MeineMails';
import { GeplanteMails } from './pages/GeplanteMails/GeplanteMails';
import { GeplanteMail } from './pages/GeplanteMails/GeplanteMail';
import { FeedbackSeite } from './pages/Feedback/FeedbackSeite';
import { Freigaben } from './pages/Freigaben/Freigaben';
import { Vorlagen } from './pages/Vorlagen/Vorlagen';
import { Handbuch } from './pages/Handbuch/Handbuch';
import { KeinZugang } from './pages/KeinZugang/KeinZugang';
import { CampaignList } from './pages/Campaigns/CampaignList';
import { CampaignEditor } from './pages/Campaigns/CampaignEditor';
import { CampaignStats } from './pages/Stats/CampaignStats';
import { SegmentList } from './pages/Segments/SegmentList';
import { SegmentEditor } from './pages/Segments/SegmentEditor';
import { TemplateList } from './pages/Templates/TemplateList';
import { TemplateEditor } from './pages/Templates/TemplateEditor';
import { ContactList } from './pages/Contacts/ContactList';
import { MediaList } from './pages/Media/MediaList';
import { Settings } from './pages/Settings/Settings';
import { DataQuality } from './pages/DataQuality/DataQuality';
import { AbmeldungenBounces } from './pages/Abmeldungen/AbmeldungenBounces';
import { RequireRole } from './components/RequireRole';
import { useAuthStore } from './stores/authStore';
import { useThemeStore } from './stores/themeStore';
import { editorPfad, istTeam } from './lib/rollen';
import type { MailRole } from './lib/supabase';

const TEAM: MailRole[] = ['admin', 'redakteur', 'viewer'];

function LoadingScreen() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-primary, #f8f7f4)', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ width: 48, height: 48, border: '3px solid var(--border-color, #d4c9b5)', borderTopColor: 'var(--primary, #6d1723)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem', margin: 0 }}>Randori Mail wird geladen...</p>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Angemeldet, aber ohne Rolle im Mailtool: freundlich erklären statt leerer Seiten.
  if (user?.ohneRolle) return <KeinZugang />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Das Team sieht Zahlen und Kampagnen, Geschäftsführung und Studioleitung ihre Mails. */
function Startseite() {
  const user = useAuthStore((s) => s.user);
  return istTeam(user) ? <Dashboard /> : <Start />;
}

function Team({ children }: { children: React.ReactNode }) {
  return <RequireRole roles={TEAM}>{children}</RequireRole>;
}

/**
 * Kampagnen-Editor nur fürs Team. Geschäftsführung und Studioleitung landen
 * hier über alte Links oder Lesezeichen — statt „nicht freigeschaltet“ geht es
 * zur Mail auf „Geplante Mails“ bzw. zum Assistenten (OG73).
 */
function KampagneOeffnen() {
  const user = useAuthStore((s) => s.user);
  const { id = '' } = useParams();
  if (user && !istTeam(user)) {
    return <Navigate to={!id || id === 'new' ? '/neue-mail' : editorPfad(user, id)} replace />;
  }
  return <Team><CampaignEditor /></Team>;
}

export default function App() {
  const { applyTheme } = useThemeStore();
  useEffect(() => { applyTheme(); }, [applyTheme]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        {/* Persönlicher Feedback-Link: außerhalb des Layouts; die Seite zeigt
            selbst die Google-Anmeldung, die Datenbank prüft sie (Migration 0051).
            Auch Angemeldete landen hier (kein PublicRoute-Umleiten). */}
        <Route path="/feedback/:token/:mailId?" element={<FeedbackSeite />} />
        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<Startseite />} />
          {/* Eine Route für neue und bestehende Mails: Beim ersten Speichern
              wechselt nur die Adresse, der Assistent behält seinen Stand. */}
          <Route path="neue-mail/:id?" element={<MailAssistent />} />
          <Route path="meine-mails" element={<MeineMails />} />
          {/* Alle mit Mailtool-Rolle; was jemand sieht, entscheidet die Datenbank. */}
          <Route path="geplante-mails" element={<GeplanteMails />} />
          <Route path="geplante-mails/:id" element={<GeplanteMail />} />
          <Route path="vorlagen" element={<Vorlagen />} />
          <Route path="handbuch" element={<Handbuch />} />
          <Route path="freigaben" element={<RequireRole redaktion><Freigaben /></RequireRole>} />
          <Route path="campaigns" element={<Team><CampaignList /></Team>} />
          {/* Eine gemeinsame Route fuer neue und bestehende Kampagnen,
              damit der Editor beim ersten Speichern nicht unmountet und der
              aktuelle Step / Form-State erhalten bleibt. */}
          <Route path="campaigns/:id" element={<KampagneOeffnen />} />
          <Route path="campaigns/:id/stats" element={<Team><CampaignStats /></Team>} />
          <Route path="segments" element={<Team><SegmentList /></Team>} />
          <Route path="segments/new" element={<RequireRole roles={['admin', 'redakteur']}><SegmentEditor /></RequireRole>} />
          <Route path="segments/:id" element={<Team><SegmentEditor /></Team>} />
          <Route path="templates" element={<Team><TemplateList /></Team>} />
          <Route path="templates/:id" element={<RequireRole roles={['admin']}><TemplateEditor /></RequireRole>} />
          <Route path="contacts" element={<Team><ContactList /></Team>} />
          <Route path="abmeldungen" element={<Team><AbmeldungenBounces /></Team>} />
          <Route path="data-quality" element={<RequireRole roles={['admin']}><DataQuality /></RequireRole>} />
          <Route path="media" element={<RequireRole roles={['admin', 'redakteur']}><MediaList /></RequireRole>} />
          <Route path="settings/*" element={<RequireRole roles={['admin']}><Settings /></RequireRole>} />
          <Route path="*" element={<div className="empty-state"><h3>Seite nicht gefunden</h3><p>Die angeforderte Seite existiert nicht.</p></div>} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
